import React, { useRef } from 'react';

import { useCallbackOne } from 'use-memo-one';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { type ForgeDoc } from '@atlassian/forge-ui-types';

import { Iframe } from '../iframe';
import { WorkerWebRuntime, isAppUsingWorkerRuntime } from '../worker-runtime';

interface WebRuntimeProps {
	setError?: (error: Error) => void;
	setForgeDoc: (forgeDoc: ForgeDoc) => void;
}

type IframeProps = Parameters<typeof Iframe>[0];

export const WebRuntime = ({
	setForgeDoc,
	setError,
	accountId,
	contextIds,
	extension,
	components,
	coreData,
	extensionData,
	extensionPayload,
	extensionViewData,
	getContextToken,
	timezone,
	locale,
	bridge,
	isEmbeddedMacro,
	embeddedMacroParentType,
	entryPoint,
	...restProps
}: WebRuntimeProps & IframeProps): React.JSX.Element => {
	const forgeReactMajorVersion = useRef<number | null>(null);

	const combinedBridge = {
		onError: ({ error }: { error: Error }) => {
			if (setError) {
				setError(error);
			}
		},
		...bridge,
		reconcile: ({ forgeDoc }: { forgeDoc: ForgeDoc }) => {
			Object.freeze(forgeDoc);
			setForgeDoc(forgeDoc);
			if (forgeReactMajorVersion.current === null && forgeDoc.forgeReactMajorVersion) {
				forgeReactMajorVersion.current = forgeDoc.forgeReactMajorVersion;
			}
		},
	};

	const getCurrentForgeReactMajorVersion = useCallbackOne(() => {
		return forgeReactMajorVersion.current;
	}, [forgeReactMajorVersion]);

	// this is essential based on feature gate for now.
	// In the future, this check should inspect extension / manifest prop to
	// determine whether to use the worker runtime or not.
	if (isAppUsingWorkerRuntime(extension, entryPoint) && fg('forge-ui-worker-runtime')) {
		return (
			<WorkerWebRuntime
				accountId={accountId}
				contextIds={contextIds}
				extension={extension}
				coreData={coreData}
				components={components}
				extensionData={extensionData}
				extensionPayload={extensionPayload}
				extensionViewData={extensionViewData}
				getContextToken={getContextToken}
				getCurrentForgeReactMajorVersion={getCurrentForgeReactMajorVersion}
				timezone={timezone}
				locale={locale}
				bridge={combinedBridge}
				appId={extension.appId!}
				entryPoint={entryPoint}
				{...restProps}
				onError={(err) => setError && setError(new Error(err))}
			/>
		);
	}

	return (
		<Iframe
			accountId={accountId}
			contextIds={contextIds}
			extension={extension}
			coreData={coreData}
			components={components}
			extensionData={extensionData}
			extensionPayload={extensionPayload}
			extensionViewData={extensionViewData}
			getContextToken={getContextToken}
			timezone={timezone}
			locale={locale}
			bridge={combinedBridge}
			isHidden={true}
			isEmbeddedMacro={isEmbeddedMacro}
			embeddedMacroParentType={embeddedMacroParentType}
			entryPoint={entryPoint}
			getCurrentForgeReactMajorVersion={getCurrentForgeReactMajorVersion}
			{...restProps}
			// The hidden UI Kit iframe defaults to height: '100%' when viewportSize is set
			// (because viewportSize makes isResizable false which disables iframeResizer, falling back to height: 100%).
			// For embedded UI Kit macros, this inflates the iframe's bodyScroll measurement,
			// causing the outer faus adf-renderer iframe to grow unexpectedly. Setting '0px' prevents this.
			height={
				(fg('platform_forge_ui_refactor_macro_parent_type')
					? !!embeddedMacroParentType
					: isEmbeddedMacro) && fg('platform_forge_ui_embedded_inline_macro')
					? '0px'
					: undefined
			}
			onError={(err) => setError && setError(new Error(err))}
		/>
	);
};
